import React, { useState } from 'react';
import { cn } from './cn';

export type AvatarSize = 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl';

export interface AvatarProps {
  src?: string | null;
  alt?: string;
  /** Used for the monogram when no image is available. */
  name?: string;
  size?: AvatarSize;
  shape?: 'circle' | 'rounded';
  className?: string;
  /** Optional tint for the monogram background. Defaults to brand. */
  tint?: 'brand' | 'neutral' | 'warm';
  /**
   * How to anchor the image inside the avatar box when `object-cover` crops.
   * Defaults to `center`. Use `top` for headshot/portrait photos so the face
   * stays in frame instead of being cropped low.
   */
  objectPosition?: 'center' | 'top';
  /**
   * Subtle inset ring around the avatar. Defaults to true (matches the look
   * across the rest of the app, which sits on white surfaces). Set false
   * when the avatar lives on a colored/branded background where the default
   * cream ring would read as a mismatched outline — supply your own outer
   * ring via wrapper if you still want visual separation.
   */
  bordered?: boolean;
}

const SIZE_MAP: Record<AvatarSize, { box: string; text: string }> = {
  xs:  { box: 'h-6 w-6',   text: 'text-[11px]' },
  sm:  { box: 'h-8 w-8',   text: 'text-caption' },
  md:  { box: 'h-10 w-10', text: 'text-body-sm' },
  lg:  { box: 'h-12 w-12', text: 'text-body-md' },
  xl:  { box: 'h-16 w-16', text: 'text-heading-md' },
  '2xl': { box: 'h-20 w-20', text: 'text-heading-lg' },
};

const TINT: Record<NonNullable<AvatarProps['tint']>, string> = {
  brand:   'bg-brand-soft text-brand',
  neutral: 'bg-surface-2 text-ink-muted',
  warm:    'bg-sev-major-soft text-sev-major-ink',
};

function monogram(name?: string): string {
  if (!name) return '—';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export const Avatar: React.FC<AvatarProps> = ({
  src, alt, name, size = 'md', shape = 'circle', tint = 'brand', className,
  objectPosition = 'center', bordered = true,
}) => {
  const [failed, setFailed] = useState(false);
  const showImage = src && !failed;
  const { box, text } = SIZE_MAP[size];
  const radius = shape === 'circle' ? 'rounded-full' : 'rounded-lg';
  const objectPositionClass = objectPosition === 'top' ? 'object-top' : 'object-center';
  return (
    <span
      className={cn(
        'relative inline-flex items-center justify-center overflow-hidden select-none shrink-0',
        bordered && 'ring-1 ring-inset ring-border-subtle',
        box, radius, className,
      )}
      aria-label={alt || name}
    >
      {showImage ? (
        <img
          src={src!}
          alt={alt || name || ''}
          className={cn('h-full w-full object-cover', objectPositionClass)}
          onError={() => setFailed(true)}
        />
      ) : (
        <span
          className={cn(
            'h-full w-full flex items-center justify-center font-semibold font-display',
            text,
            TINT[tint],
          )}
        >
          {monogram(name)}
        </span>
      )}
    </span>
  );
};
